<template>
  <div>
    <div v-if="hasPaginationFilter">
      <div class="mb-4 flex flex-wrap gap-4">
        <div class="flex flex-1 flex-col gap-1">
          <label class="tb-label">
            Current Page
          </label>

          <div class="flex w-full max-w-[150px] items-stretch p-0">
            <button
              class="tb-btn tb-btn-contained medium-em"
              style="border-top-right-radius: 0; border-bottom-right-radius: 0; border-top-left-radius: 0.25rem; border-bottom-left-radius: 0.25rem;"
              :disabled="pagination.page <= 1"
              data-entity="producer-editor-pageBack"
              data-test="graphics-editor-dynamicList-previousPage"
              @click="incrementPage(-1)"
            >
              <span class="fas fa-backward" />
            </button>

            <input
              type="number"
              :min="1"
              v-model.number="pagination.page"
              class="tb-input flex-1 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              style="border-radius: 0;"
              data-entity="producer-editor-setPage"
              data-test="graphics-editor-dynamicList-pageInput"
            />

            <button
              class="tb-btn tb-btn-contained medium-em"
              style="border-top-left-radius: 0; border-bottom-left-radius: 0; border-top-right-radius: 0.25rem; border-bottom-right-radius: 0.25rem;"
              data-entity="producer-editor-pageForward"
              data-test="graphics-editor-dynamicList-nextPage"
              @click="incrementPage(1)"
            >
              <span class="fas fa-forward" />
            </button>
          </div>
        </div>

        <div class="flex flex-1 flex-col gap-1">
          <label class="tb-label">
            Start From Row
          </label>

          <div class="flex w-full max-w-[150px] items-stretch p-0">
            <button
              class="tb-btn tb-btn-contained medium-em"
              style="border-top-right-radius: 0; border-bottom-right-radius: 0; border-top-left-radius: 0.25rem; border-bottom-left-radius: 0.25rem;"
              :disabled="paginationOffset <= 1"
              data-entity="producer-editor-startFromRowBack"
              data-test="producer-editor-dataList-startFromRowBack"
              @click="paginationOffset--"
            >
              <span class="fas fa-backward" />
            </button>

            <input
              v-model.number="paginationOffset"
              type="number"
              :min="1"
              placeholder="1"
              class="tb-input flex-1 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              style="border-radius: 0;"
              data-entity="producer-editor-setStartFromRow"
              data-test="producer-editor-dataList-startFromRow"
            />

            <button
              class="tb-btn tb-btn-contained medium-em"
              style="border-top-left-radius: 0; border-bottom-left-radius: 0; border-top-right-radius: 0.25rem; border-bottom-right-radius: 0.25rem;"
              data-entity="producer-editor-startFromRowForward"
              data-test="producer-editor-dataList-startFromRowForward"
              @click="paginationOffset++"
            >
              <span class="fas fa-forward" />
            </button>
          </div>
        </div>

        <div class="flex flex-1 flex-col gap-1">
          <label class="tb-label">
            Rows Per Page
          </label>

          <div class="flex w-full max-w-[150px] items-stretch p-0">
            <button
              class="tb-btn tb-btn-contained medium-em"
              style="border-top-right-radius: 0; border-bottom-right-radius: 0; border-top-left-radius: 0.25rem; border-bottom-left-radius: 0.25rem;"
              :disabled="paginationLimit <= 1"
              data-entity="producer-editor-rowsPerPageDown"
              data-test="producer-editor-dataList-rowsPerPageDown"
              @click="paginationLimit--"
            >
              <span class="fas fa-backward" />
            </button>

            <input
              v-model.number="paginationLimit"
              type="number"
              :min="1"
              placeholder="10"
              class="tb-input flex-1 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              style="border-radius: 0;"
              data-entity="producer-editor-setRowsPerPage"
              data-test="producer-editor-dataList-rowsPerPage"
            />

            <button
              class="tb-btn tb-btn-contained medium-em"
              style="border-top-left-radius: 0; border-bottom-left-radius: 0; border-top-right-radius: 0.25rem; border-bottom-right-radius: 0.25rem;"
              data-entity="producer-editor-rowsPerPageUp"
              data-test="producer-editor-dataList-rowsPerPageUp"
              @click="paginationLimit++"
            >
              <span class="fas fa-forward" />
            </button>
          </div>
        </div>
      </div>

      <hr />

      <div class="tb-form-group mt-4">
        <label class="tb-label mb-2">
          Preview
        </label>

        <div v-if="isLoadingPreview" class="text-center padding-md">
          Loading preview...
        </div>

        <div v-else-if="previewError" class="text-danger padding-md">
          {{ previewError }}
        </div>

        <div
          v-else-if="previewData.length > 0"
          class="data-list__preview"
          data-test="producer-editor-dataList-preview"
        >
          <table class="data-list__preview-table">
            <tbody>
              <tr
                v-for="(row, rowIdx) in previewData"
                :key="rowIdx"
                class="data-list__preview-row"
              >
                <td class="data-list__preview-row-num">{{ rowIdx + 1 }}</td>
                <td
                  v-for="(cell, cellIdx) in row"
                  :key="cellIdx"
                  class="data-list__preview-cell"
                >
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="padding-md text-muted">
          No preview data available
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GoogleService from '../../../../services/google';

export default {
  props: {
    element: {
      type: Object,
      required: true,
    },
    template: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoadingPreview: false,
      previewError: null,
      previewData: [],
      previewHeaders: [],
      previewRequestId: 0,
    };
  },

  computed: {
    filters() {
      return this.element?.data?.filters || [];
    },

    hasPaginationFilter() {
      return !!this.filters.find((f) => f.type === 'pagination');
    },

    pagination() {
      return this.filters.find((filter) => filter.type === 'pagination');
    },

    paginationIndex() {
      return this.filters.findIndex((filter) => filter.type === 'pagination');
    },

    paginationOffset: {
      get() {
        return this.pagination?.offset || 1;
      },
      set(v) {
        const { filters } = this;
        const pagination = filters.find((f) => f.type === 'pagination');
        const n = Number.isFinite(v) ? Math.floor(v) : 1;
        pagination.offset = Math.max(1, n);

        this.$emit('value', {
          value: filters,
          field: 'data.filters',
        });
      },
    },

    paginationLimit: {
      get() {
        return this.pagination?.limit || 10;
      },
      set(v) {
        const { filters } = this;
        const pagination = filters.find((f) => f.type === 'pagination');
        const n = Number.isFinite(v) ? Math.floor(v) : 1;
        pagination.limit = Math.max(1, n);

        this.$emit('value', {
          value: filters,
          field: 'data.filters',
        });
      },
    },

    sheetDataElement() {
      // Find the parent sheet-data element
      const elements = this.template?.elements || [];

      // Check if this element is the parent by looking for children in el.elements
      return elements.find((el) => el?.elements?.some((e) => e.id === this.element.id));
    },

    childElements() {
      // Get direct children of this data-list
      const childElementIds = this.element?.elements || [];
      const allElements = this.template?.elements || [];
      return childElementIds
        .map((id) => allElements.find((el) => el.id === id))
        .filter(Boolean);
    },

    mappedColumns() {
      // Extract which columns are mapped from child elements
      const columns = new Set();
      this.childElements.forEach((child) => {
        const dataKeyMap = child?.data?.dataKeyMap || {};
        Object.values(dataKeyMap).forEach((colIdx) => {
          if (typeof colIdx === 'number') {
            columns.add(colIdx);
          }
        });
      });

      return Array.from(columns).sort((a, b) => a - b);
    },
  },

  watch: {
    'pagination.page': 'fetchPreviewData',
    'pagination.offset': 'fetchPreviewData',
    'pagination.limit': 'fetchPreviewData',
    sheetDataElement: {
      immediate: true,
      handler: 'fetchPreviewData',
    },
  },

  methods: {
    incrementPage(amount) {
      const { filters } = this;
      const pagination = filters.find((f) => f.type === 'pagination');
      pagination.page += amount;

      this.$emit('value', {
        value: filters,
        field: 'data.filters',
      });
    },

    async fetchPreviewData() {
      this.previewRequestId += 1;
      const requestId = this.previewRequestId;

      if (!this.sheetDataElement || !this.hasPaginationFilter) {
        this.previewData = [];
        this.previewHeaders = [];
        return;
      }

      this.isLoadingPreview = true;
      this.previewError = null;

      try {
        const { data } = this.sheetDataElement;
        let sheetData = [];

        // Fetch sheet data based on type
        if (data.accountId) {
          // Private sheet
          const resp = await GoogleService.fetchPrivateSheetData({
            accountId: data.accountId,
            workbookId: data.workbookId,
            sheetId: data.sheetId,
            cache: true,
          });

          sheetData = resp?.values || [];
        } else if (data.url) {
          // Public sheet
          const resp = await GoogleService.fetchPublicSheetData({
            url: data.url,
            sheet: data.sheet,
            key: this.sheetDataElement.id,
          });

          sheetData = resp?.workbook?.data || [];
        }

        if (requestId !== this.previewRequestId) return;

        if (!sheetData || sheetData.length === 0) {
          this.previewData = [];
          this.previewHeaders = [];
          return;
        }

        // Apply pagination filtering
        const { offset = 1, limit = 10, page = 1 } = this.pagination;
        const start = (page - 1) * limit;
        const end = start + limit;

        // Get filtered data
        const filteredData = sheetData
          .slice(offset - 1)
          .slice(start, end);

        // Extract only the mapped columns
        if (this.mappedColumns.length > 0) {
          // Get headers from first row of original data
          const headerRow = sheetData[0] || [];
          this.previewHeaders = this.mappedColumns.map((colIdx) => {
            const cell = headerRow[colIdx];
            return this.getCellValue(cell) || `Column ${colIdx}`;
          });

          // Map data rows to only include mapped columns
          this.previewData = filteredData.map((row) =>
            this.mappedColumns.map((colIdx) => this.getCellValue(row[colIdx])),
          );
        } else {
          // No columns mapped, show all data
          const maxCols = Math.max(...sheetData.map((r) => r.length));
          const headerRow = sheetData[0] || [];

          this.previewHeaders = Array.from({ length: maxCols }, (_, i) =>
            this.getCellValue(headerRow[i]) || `Column ${i}`,
          );

          this.previewData = filteredData.map((row) =>
            Array.from({ length: maxCols }, (_, i) => this.getCellValue(row[i])),
          );
        }
      } catch (err) {
        if (requestId !== this.previewRequestId) return;
        console.error('Error fetching preview data:', err);
        this.previewError = 'Failed to load preview data';
        this.previewData = [];
        this.previewHeaders = [];
      } finally {
        if (requestId === this.previewRequestId) {
          this.isLoadingPreview = false;
        }
      }
    },

    getCellValue(cell) {
      if (cell == null) return '';
      if (typeof cell === 'string') return cell;
      if (cell.text !== undefined) return cell.text;
      if (cell.img !== undefined) return cell.img;
      return String(cell);
    },
  },
};
</script>

<style lang="scss" scoped>
.data-list__preview {
  max-height: 25rem;
  overflow: auto;
  border: 1px solid #3a3a3a;
  border-radius: 0.25rem;
  background: #1e1e1e;
}

.data-list__preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.data-list__preview-row {
  &:nth-child(odd) {
    background: #1e1e1e;
  }

  &:nth-child(even) {
    background: #252525;
  }

  &:hover {
    background: #2d2d2d;
  }
}

.data-list__preview-row-num {
  padding: 0.5rem 0.75rem;
  text-align: center;
  font-weight: 600;
  color: #888;
  border-right: 1px solid #3a3a3a;
  border-bottom: 1px solid #3a3a3a;
  min-width: 2.5rem;
  position: sticky;
  left: 0;
  z-index: 1;

  .data-list__preview-row:nth-child(odd) & {
    background: #1e1e1e;
  }

  .data-list__preview-row:nth-child(even) & {
    background: #252525;
  }
}

.data-list__preview-cell {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #3a3a3a;
  border-right: 1px solid #3a3a3a;
  color: #d0d0d0;
  max-width: 18.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }
}

.text-muted {
  color: #888;
}
</style>
